import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { useContract } from '../../context/ContractContext';
import { useNotification } from '../../context/NotificationContext';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import PackageSelector from '../../components/invest/PackageSelector/PackageSelector';
import Button from '../../components/common/Button/Button';
import Input from '../../components/common/Input/Input';
import { DEFAULT_REFERRER_ID, POSITIONS, isRootId } from '../../config/packages';
import { formatNumber } from '../../utils/formatters';
import { getHashParam } from '../../utils/helpers';
import logo from '../../assets/logo-mark.png';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isConnected, account } = useWallet();
  const { 
    isRegistered,
    isCheckingRegistration,
    tokenBalance, 
    tokenAllowance,
    approveTokens,
    registerAndInvest,
    getUserInfo,
    isLoading
  } = useContract();
  const { error: showError, success: showSuccess, info: showInfo } = useNotification();

  const [sponsorId, setSponsorId] = useState('');
  const [position, setPosition] = useState(POSITIONS.LEFT);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!isConnected) {
      navigate('/');
      return;
    }
    // Only redirect to dashboard AFTER registration check is complete
    if (!isCheckingRegistration && isRegistered) {
      navigate('/dashboard');
      return;
    }
  }, [isConnected, isRegistered, isCheckingRegistration, navigate]);

  useEffect(() => {
    // Check for referral ID in URL
    const ref = getHashParam('ref');
    const pos = getHashParam('pos');
    if (ref && !isRootId(ref)) {
      setSponsorId(ref);
    }
    if (pos === 'right' || pos === '1') {
      setPosition(POSITIONS.RIGHT);
    }
  }, [location]);

  // Empty or the hidden contract root (1000) -> default sponsor
  const effectiveSponsor = !sponsorId || isRootId(sponsorId) ? DEFAULT_REFERRER_ID : parseInt(sponsorId, 10);

  // Show loading while checking registration
  if (isCheckingRegistration) {
    return (
      <PageContainer title="Register" showBack>
        <div className="register">
          <div className="register-header">
            <div className="register-logo-container">
              <img src={logo} alt="Gainforce" className="register-logo" />
            </div>
            <h2 className="register-title">Please Wait</h2>
            <p className="register-subtitle">Checking your account status...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  const handleRegister = async () => {
    if (!selectedPackage) {
      showError('Please select a package');
      return;
    }

    const hasBalance = parseFloat(tokenBalance) >= parseFloat(selectedPackage);
    if (!hasBalance) {
      showError('Insufficient USDT balance');
      return;
    }

    setProcessing(true);

    try {
      // Check the sponsor BEFORE approving, so a bad ID doesn't cost an approval tx
      const sponsorInfo = await getUserInfo(effectiveSponsor);
      if (!sponsorInfo?.active) {
        showError(`Sponsor #${effectiveSponsor} does not exist`);
        setProcessing(false);
        return;
      }

      // Check if approval is needed
      const needsApproval = parseFloat(tokenAllowance) < parseFloat(selectedPackage);
      
      if (needsApproval) {
        showInfo('Step 1/2: Approving USDT...');
        const approveSuccess = await approveTokens(selectedPackage);
        if (!approveSuccess) {
          setProcessing(false);
          return;
        }
        showSuccess('Approval successful! Registering...');
      }

      // Now register
      showInfo(needsApproval ? 'Step 2/2: Registering...' : 'Registering...');
      const success = await registerAndInvest(effectiveSponsor, position, selectedPackage);
      
      if (success) {
        navigate('/dashboard');
      }
    } catch (err) {
      showError('Registration failed');
    } finally {
      setProcessing(false);
    }
  };

  const hasBalance = parseFloat(tokenBalance) >= parseFloat(selectedPackage || 0);

  const nextStep = () => {
    if (step === 1 && !sponsorId && !DEFAULT_REFERRER_ID) {
      showError('Please enter a sponsor ID');
      return;
    }
    if (step < 3) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <PageContainer title="Register" showBack>
      <div className="register">
        <div className="register-header">
          <div className="register-logo-container">
            <img src={logo} alt="Gainforce" className="register-logo" />
          </div>
          <h2 className="register-title">Join Gainforce</h2>
          <p className="register-subtitle">Start your investment journey today</p>
        </div>

        <div className="register-balance-card">
          <div className="balance-info">
            <span className="balance-icon">💰</span>
            <div className="balance-details">
              <span className="balance-label">Your USDT Balance</span>
              <span className="balance-value">{formatNumber(tokenBalance)} USDT</span>
            </div>
          </div>
        </div>

        <div className="register-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Referral</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Position</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Package</span>
          </div>
        </div>

        <div className="register-form">
          {step === 1 && (
            <div className="form-step">
              <div className="step-header">
                <span className="step-icon">👤</span>
                <h3 className="step-title">Referral Information</h3>
                <p className="step-description">Enter your sponsor's ID or use the default</p>
              </div>
              <Input
                label="Sponsor ID"
                type="number"
                placeholder={`Default: ${DEFAULT_REFERRER_ID}`}
                value={sponsorId}
                onChange={(e) => setSponsorId(e.target.value)}
                hint="Leave empty to use default sponsor"
              />
              <div className="step-actions">
                <Button fullWidth size="lg" onClick={nextStep}>
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="form-step">
              <div className="step-header">
                <span className="step-icon">🌳</span>
                <h3 className="step-title">Select Position</h3>
                <p className="step-description">Choose which side of your sponsor's binary tree</p>
              </div>
              <div className="position-selector">
                <button
                  className={`position-card ${position === POSITIONS.LEFT ? 'selected' : ''}`}
                  onClick={() => setPosition(POSITIONS.LEFT)}
                >
                  <div className="position-icon-wrapper left">
                    <span className="position-icon">⬅️</span>
                  </div>
                  <span className="position-name">Left Team</span>
                  <span className="position-desc">Join the left branch</span>
                  {position === POSITIONS.LEFT && <span className="position-check">✓</span>}
                </button>
                <button
                  className={`position-card ${position === POSITIONS.RIGHT ? 'selected' : ''}`}
                  onClick={() => setPosition(POSITIONS.RIGHT)}
                >
                  <div className="position-icon-wrapper right">
                    <span className="position-icon">➡️</span>
                  </div>
                  <span className="position-name">Right Team</span>
                  <span className="position-desc">Join the right branch</span>
                  {position === POSITIONS.RIGHT && <span className="position-check">✓</span>}
                </button>
              </div>
              <div className="step-actions dual">
                <Button variant="secondary" onClick={prevStep}>
                  Back
                </Button>
                <Button onClick={nextStep}>
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="form-step">
              <div className="step-header">
                <span className="step-icon">💎</span>
                <h3 className="step-title">Select Package</h3>
                <p className="step-description">Choose your investment amount</p>
              </div>
              <PackageSelector
                selected={selectedPackage}
                onSelect={setSelectedPackage}
              />

              {selectedPackage && (
                <div className="register-summary">
                  <h4 className="summary-title">Investment Summary</h4>
                  <div className="summary-row">
                    <span>Package</span>
                    <span className="summary-value">{selectedPackage} USDT</span>
                  </div>
                  <div className="summary-row">
                    <span>Position</span>
                    <span className="summary-value">{position === POSITIONS.LEFT ? '⬅️ Left' : '➡️ Right'}</span>
                  </div>
                  <div className="summary-row">
                    <span>Sponsor ID</span>
                    <span className="summary-value">#{effectiveSponsor}</span>
                  </div>
                  <div className="summary-divider"></div>
                  <div className="summary-row highlight">
                    <span>300% Cap Returns</span>
                    <span className="summary-value">{parseFloat(selectedPackage) * 3} USDT</span>
                  </div>
                </div>
              )}

              <div className="step-actions dual">
                <Button variant="secondary" onClick={prevStep}>
                  Back
                </Button>
                <Button
                  onClick={handleRegister}
                  loading={isLoading || processing}
                  disabled={!selectedPackage || !hasBalance || processing}
                >
                  {processing ? 'Processing...' : 'Register'}
                </Button>
              </div>

              {!hasBalance && selectedPackage && (
                <p className="register-error">
                  <span>⚠️</span> Insufficient USDT balance
                </p>
              )}
            </div>
          )}
        </div>

        <div className="register-features">
          <div className="feature-item">
            <span className="feature-icon">🤖</span>
            <span className="feature-text">AI Trading</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">📈</span>
            <span className="feature-text">Daily ROI</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">🔒</span>
            <span className="feature-text">Secure</span>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default Register;